import { useState, useEffect } from "react";
import { ChevronDown, Cookie, X } from "lucide-react";

// iter170: GDPR cookie consent banner.
// Renders once per browser (keyed by localStorage.mm_cookie_consent). Bump
// CONSENT_VERSION to force a re-prompt if the cookie policy changes.
//
// Today the app only ships essential (Functional) cookies — session auth
// + cart persistence — so the banner is notice-only. The Marketing toggle
// is a placeholder for future analytics/ads pixels; nothing is loaded
// against it yet. Downstream integrations should read
// `getCookieConsent()` before booting.
const STORAGE_KEY = "mm_cookie_consent";
const CONSENT_VERSION = 1;

export function getCookieConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.v !== CONSENT_VERSION) return null;
    return parsed;
  } catch { return null; }
}

const PRIVACY_URL = "https://maitree-ev.org/privacy-statement-eu/";
const COOKIE_POLICY_URL = "https://maitree-ev.org/cookie-policy-eu/";
const IMPRINT_URL = "https://maitree-ev.org/imprint/";

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [expanded, setExpanded] = useState(null); // "functional" | "marketing" | null
  // iter171: floating minimize-able modal. Once the user has decided (or
  // minimizes without deciding), the modal collapses to a small floating
  // cookie button anchored bottom-left. Clicking it re-opens the modal
  // — that's the "reopen" affordance the footer would otherwise have
  // needed. `hasConsent` drives whether the launcher is shown post-decision.
  const [hasConsent, setHasConsent] = useState(() => !!getCookieConsent());

  useEffect(() => {
    // Delay one tick so hydration / router mount finishes first — the
    // banner sliding in *after* the page has painted feels smoother than
    // it appearing simultaneously with the shop content.
    const t = setTimeout(() => {
      const consent = getCookieConsent();
      if (!consent) setVisible(true);
      // Pre-fill Marketing toggle from a previous choice so re-opening
      // doesn't silently reset the user's saved preference.
      if (consent) setMarketing(!!consent.marketing);
    }, 250);
    return () => clearTimeout(t);
  }, []);

  const persist = (marketingOn) => {
    const payload = {
      v: CONSENT_VERSION,
      essential: true,          // always on — required for the app to function
      marketing: !!marketingOn,
      ts: new Date().toISOString(),
    };
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(payload)); } catch { /* private mode */ }
    // Emit an event so downstream integrations can react without a reload.
    try { window.dispatchEvent(new CustomEvent("cookieConsent", { detail: payload })); } catch {}
    setHasConsent(true);
    setVisible(false);
  };

  const onAccept = () => persist(true);
  const onDeny = () => persist(false);
  const onSave = () => persist(marketing);
  // iter171: minimize without persisting a decision. GDPR-safe because
  // no consent is recorded — the launcher stays visible and clicking it
  // brings the modal back with fresh Accept/Deny/Save actions.
  const onMinimize = () => setVisible(false);
  const onReopen = () => setVisible(true);

  // Floating launcher — persistent so users can revisit their choice.
  // Visible whenever the modal is minimized, either post-decision or
  // pre-decision (a rare case the user actively dismissed without deciding).
  const launcher = !visible ? (
    <button
      onClick={onReopen}
      className="cookie-fab fixed bottom-4 left-4 z-[81] w-11 h-11 rounded-full bg-white shadow-lg border border-slate-200 flex items-center justify-center text-[#1e3a5f] hover:bg-slate-50 hover:scale-105 transition-transform"
      data-testid="cookie-launcher-btn"
      title={hasConsent ? "Manage cookie preferences" : "Cookie consent"}
      aria-label="Open cookie preferences"
    >
      <Cookie className="w-5 h-5" />
    </button>
  ) : null;

  if (!visible) return launcher;

  return (
    <>
      {/* Backdrop — soft dim so the banner doesn't feel like a floating panel
          disconnected from the page. Not click-to-dismiss (would violate
          GDPR "no default consent" rule). */}
      <div className="fixed inset-0 bg-black/30 z-[80] pointer-events-none" aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="cookie-banner-title"
        className="fixed left-1/2 -translate-x-1/2 bottom-6 sm:bottom-8 z-[81] w-[min(94vw,540px)]"
        data-testid="cookie-banner"
      >
        <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 p-5 sm:p-6 relative">
          {/* iter171: minimize button — collapses the modal to the
              floating launcher without recording consent. */}
          <button
            onClick={onMinimize}
            className="absolute top-3 right-3 w-7 h-7 rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-700 flex items-center justify-center transition-colors"
            data-testid="cookie-minimize-btn"
            title="Minimize"
            aria-label="Minimize cookie preferences"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-start gap-3 mb-3 pr-8">
            <img src="/logo.png" alt="" className="w-8 h-8 rounded-md object-contain shrink-0" onError={(e) => { e.target.style.display = "none"; }} />
            <h2 id="cookie-banner-title" className="text-lg font-extrabold text-[#1e3a5f]">Manage Cookie Consent</h2>
          </div>
          <p className="text-[13px] text-slate-600 leading-relaxed mb-4">
            To provide the best experiences, we use technologies like cookies to store and/or access
            device information. Consenting to these technologies will allow us to process data such as
            browsing behavior or unique IDs on this site. Not consenting or withdrawing consent, may
            adversely affect certain features and functions.
          </p>

          {/* Functional row — always active, toggle disabled */}
          <div className="border-t border-slate-100 py-3">
            <button
              onClick={() => setExpanded(x => x === "functional" ? null : "functional")}
              className="w-full flex items-center justify-between text-left"
              data-testid="cookie-toggle-functional"
              aria-expanded={expanded === "functional"}
            >
              <span className="font-semibold text-[#1e3a5f] text-sm">Functional</span>
              <span className="flex items-center gap-2">
                <span className="text-[11px] font-bold text-emerald-600">Always active</span>
                <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${expanded === "functional" ? "rotate-180" : ""}`} />
              </span>
            </button>
            {expanded === "functional" && (
              <p className="text-[12px] text-slate-500 mt-2 leading-relaxed" data-testid="cookie-detail-functional">
                Required so you stay logged in, so your cart survives a page refresh, and so critical
                pages (payments, ticket delivery) work. These cookies never leave our servers.
              </p>
            )}
          </div>

          {/* Marketing row — user-controlled */}
          <div className="border-t border-slate-100 py-3 mb-1">
            <button
              onClick={() => setExpanded(x => x === "marketing" ? null : "marketing")}
              className="w-full flex items-center justify-between text-left"
              data-testid="cookie-toggle-marketing"
              aria-expanded={expanded === "marketing"}
            >
              <span className="font-semibold text-[#1e3a5f] text-sm">Marketing</span>
              <span className="flex items-center gap-2">
                {/* Segmented toggle — click stops propagation so the row's
                    accordion behaviour doesn't fight the switch. */}
                <span
                  role="switch"
                  aria-checked={marketing}
                  tabIndex={0}
                  onClick={(e) => { e.stopPropagation(); setMarketing(v => !v); }}
                  onKeyDown={(e) => { if (e.key === " " || e.key === "Enter") { e.preventDefault(); setMarketing(v => !v); } }}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors cursor-pointer ${marketing ? "bg-[#f5a623]" : "bg-slate-300"}`}
                  data-testid="cookie-switch-marketing"
                >
                  <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${marketing ? "translate-x-4" : "translate-x-0.5"}`} />
                </span>
                <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${expanded === "marketing" ? "rotate-180" : ""}`} />
              </span>
            </button>
            {expanded === "marketing" && (
              <p className="text-[12px] text-slate-500 mt-2 leading-relaxed" data-testid="cookie-detail-marketing">
                Used to measure ad performance and personalize what you see on partner sites. Currently
                inactive — no marketing cookies are set today. Enabling this only affects future
                integrations.
              </p>
            )}
          </div>

          <div className="grid grid-cols-3 gap-2 mt-4">
            <button
              onClick={onAccept}
              className="py-2 rounded-lg bg-[#1e3a5f] text-white text-sm font-semibold hover:bg-[#15294a] transition-colors"
              data-testid="cookie-accept-btn"
            >
              Accept
            </button>
            <button
              onClick={onDeny}
              className="py-2 rounded-lg border border-slate-300 text-slate-700 text-sm font-semibold hover:bg-slate-50 transition-colors"
              data-testid="cookie-deny-btn"
            >
              Deny
            </button>
            <button
              onClick={onSave}
              className="py-2 rounded-lg border border-slate-300 text-slate-700 text-sm font-semibold hover:bg-slate-50 transition-colors"
              data-testid="cookie-save-btn"
            >
              Save Preferences
            </button>
          </div>

          <div className="mt-4 pt-3 border-t border-slate-100 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[11px]">
            <a href={COOKIE_POLICY_URL} target="_blank" rel="noopener noreferrer" className="text-[#0ea5e9] hover:underline">Cookie Policy</a>
            <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" className="text-[#0ea5e9] hover:underline" data-testid="cookie-privacy-link">Privacy Statement</a>
            <a href={IMPRINT_URL} target="_blank" rel="noopener noreferrer" className="text-[#0ea5e9] hover:underline">Imprint</a>
          </div>
        </div>
      </div>
    </>
  );
}
